import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import LoginPage from './components/LoginPage';
import PortalShell from './components/PortalShell';
import Overview from './components/Overview';
import SwilDocuments from './components/SwilDocuments';
import { portalLogout } from './services/swil';
import ProductSearch from './components/ProductSearch';
import CsvOrder from './components/CsvOrder';
import ProductBatchWiseOrder from './components/ProductSearchBatchWise';
import Cart from './components/Cart';
import ViewOrder from './components/ViewOrder';
import OrderDetail from './components/OrderDetail';
import Outstanding from './components/Outstanding';
import api from './services/api';


const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [products, setProducts] = useState(null);

  const [restoring, setRestoring] = useState(true);
  useEffect(() => {
    let mounted = true;
    api.get('session.php').then(async ({ data }) => {
      if (!mounted) return;
      const profile = data.profile;
      setCustomer(profile);
      localStorage.setItem('customer', JSON.stringify(profile));
      setIsLoggedIn(true);
      try { setCartItems(JSON.parse(localStorage.getItem('cartItems') || '[]')); } catch { setCartItems([]); }
    }).catch((error) => {
      if (!mounted) return;
      // Only the server saying "not signed in" ends the sign-in; a network hiccup keeps the saved one.
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem('customer')); } catch { /* Invalid saved profile. */ }
      if (error.response?.status !== 401 && saved?.customer_id) {
        setCustomer(saved);
        try { setCartItems(JSON.parse(localStorage.getItem('cartItems') || '[]')); } catch { setCartItems([]); }
        setIsLoggedIn(true);
        return;
      }
      window.history.replaceState(null, '', '/login');
      localStorage.removeItem('customer');
      localStorage.removeItem('salesman');
      localStorage.removeItem('cartItems');
    }).finally(() => { if (mounted) setRestoring(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!restoring && isLoggedIn) localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems, restoring, isLoggedIn]);

  const handleLogin = (customerData) => {
    window.history.replaceState(null, '', '/home');
    setCustomer(customerData);
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    try { await portalLogout(); } catch (error) { alert(error.message); return; }
    localStorage.removeItem('customer');
    localStorage.removeItem('cartItems');
    window.history.replaceState(null, '', '/login');
    setCustomer(null);
    setIsLoggedIn(false);
    setCartItems([]);
  };

  const handleAddToCart = (product) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.product_id === product.product_id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.product_id === product.product_id ? { ...item, quantity: product.quantity } : item
        );
      }
      return [...prevItems, product];
    });
  };

  const handleUpdateCartItem = (updatedProduct) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.product_id === updatedProduct.product_id ? updatedProduct : item
      )
    );
  };

  const handleRemoveFromCart = (productId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.product_id !== productId));
  };

  const handleClearCart = () => {
    setCartItems([]);
  };

  const cartItemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  if (restoring) return <Box p={3}>Restoring session…</Box>;

  if (!isLoggedIn) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <Router>
      <PortalShell role="customer" customer={customer} cartCount={cartItemCount} onLogout={handleLogout}>
            <Routes>
              <Route path="/ledger" element={<SwilDocuments kind="ledger" customer={customer} />} />
              <Route path="/invoices" element={<SwilDocuments kind="invoices" customer={customer} />} />
              <Route path="/credit-notes" element={<SwilDocuments kind="credit-notes" customer={customer} />} />
              <Route path="/account-statement" element={<SwilDocuments kind="outstanding" customer={customer} />} />
              <Route path="/" element={<Navigate to="/home" />}  />  {/* Redirect root to home */}
              <Route path="/home" element={<Overview customer={customer} role="customer" cartItems={cartItems} onAddToCart={handleAddToCart} onUpdateCartItem={handleUpdateCartItem} onRemoveFromCart={handleRemoveFromCart} />} /> {/* Default Home Page */}
              <Route path="/search" element={<ProductSearch onAddToCart={handleAddToCart} cartItems={cartItems || []} products={products} setProducts={setProducts}  onRemoveFromCart={handleRemoveFromCart} onUpdateCartItem={handleUpdateCartItem} customer={customer} onClearCart={handleClearCart}/>} />
              <Route path="/csv-order" element={<CsvOrder customer={customer} products={products} setProducts={setProducts} cartItems={cartItems} onAddToCart={handleAddToCart} />} />
              <Route path="/Expiry" element={<ProductBatchWiseOrder onAddToCart={handleAddToCart} cartItems={cartItems || []} products={products} setProducts={setProducts}  onRemoveFromCart={handleRemoveFromCart} onUpdateCartItem={handleUpdateCartItem} customer={customer} onClearCart={handleClearCart}/>} />
              
              <Route path="/cart" element={<Cart cartItems={cartItems} onRemoveFromCart={handleRemoveFromCart} onUpdateCartItem={handleUpdateCartItem} customer={customer} onClearCart={handleClearCart} />} />
              <Route path="/orders" element={<ViewOrder customer={customer} />} />
              <Route path="/order/:orderId" element={<OrderDetail customer={customer} />} />
              <Route path="/outstanding" element={<Outstanding customer={customer} />} />
              <Route path="*" element={<Navigate to="/home" />} /> {/* Redirect all unknown paths to home */}
            </Routes>
      </PortalShell>
    </Router>
  );
};

export default App;
